import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight } from "lucide-react";

export default function Gallery({ images = [], settings }) {
  const [lightbox, setLightbox] = useState(null); // index or null

  if (!images || images.length === 0) return null;

  const openAt = (i) => setLightbox(i);
  const close = () => setLightbox(null);
  const prev = () =>
    setLightbox((i) => (i === null ? null : (i - 1 + images.length) % images.length));
  const next = () =>
    setLightbox((i) => (i === null ? null : (i + 1) % images.length));

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-24 md:py-32 bg-[var(--dough-warm)]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="section-rule" />
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--charcoal)] font-semibold leading-[1.05]">
              Straight from the kitchen
            </h2>
            <p className="mt-4 text-[var(--charcoal-soft)] max-w-xl text-base md:text-lg">
              A few of our signature plates, photographed just before they hit
              the table. Hungry yet?
            </p>
          </div>
        </div>

        {/* Bento layout: first image spans 2 cols on desktop */}
        <div
          data-testid="gallery-grid"
          className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 auto-rows-[200px] md:auto-rows-[260px]"
        >
          {images.map((img, i) => {
            const isFeatured = i === 0;
            return (
              <motion.button
                key={img.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                onClick={() => openAt(i)}
                data-testid={`gallery-item-${i}`}
                className={`group relative overflow-hidden rounded-2xl bg-[var(--charcoal)] focus:outline-none focus:ring-2 focus:ring-[var(--terracotta)] ${
                  isFeatured ? "col-span-2 row-span-2" : ""
                }`}
              >
                <img
                  src={img.data_url}
                  alt={img.caption || "Emilio's dish"}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  style={{ filter: "saturate(1.1) contrast(1.03)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-80 group-hover:opacity-95 transition-opacity" />
                {img.caption && (
                  <div className="absolute bottom-0 inset-x-0 p-4 text-left text-white">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#F5D7A6] mb-1">
                      Emilio&apos;s Kitchen
                    </div>
                    <div className="font-serif text-lg md:text-xl leading-tight">
                      {img.caption}
                    </div>
                  </div>
                )}
              </motion.button>
            );
          })}
        </div>

        {settings?.order_url && (
          <div className="mt-12 text-center">
            <a
              href={settings.order_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gallery-order-btn"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[var(--terracotta)] hover:bg-[var(--terracotta-hover)] text-white font-semibold transition-all hover:-translate-y-0.5"
            >
              Order these dishes
            </a>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && images[lightbox] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[80] bg-black/92 flex items-center justify-center p-4"
            onClick={close}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                close();
              }}
              data-testid="gallery-close"
              aria-label="Close"
              className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            >
              <X size={20} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              data-testid="gallery-prev"
              aria-label="Previous"
              className="absolute left-3 md:left-6 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            >
              <ArrowLeft size={20} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              data-testid="gallery-next"
              aria-label="Next"
              className="absolute right-3 md:right-6 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            >
              <ArrowRight size={20} />
            </button>
            <motion.div
              key={lightbox}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              className="max-w-6xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={images[lightbox].data_url}
                alt={images[lightbox].caption || ""}
                className="w-full max-h-[82vh] object-contain rounded-lg"
              />
              {images[lightbox].caption && (
                <div className="mt-4 text-center text-white/90 font-serif italic text-lg">
                  {images[lightbox].caption}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
